import { HostListener, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { bearing, destination, point } from '@turf/turf';
import 'leaflet';
import { Observable } from 'rxjs/Observable';

import { CBMError, Contract, Siterecord, SiterecordComment, VildVersion } from 'app/models';
import {
  ConfigurationStatusType,
  ContractType,
  DataProviderType,
  FcdEncodingStatusType,
  LocationOverviewType,
  LocationType,
  VildEncodingStatusType
} from 'app/models/enums';
import { OpenLRExtendedPoint } from 'app/models/openlr';
import { ShapeStyle } from 'app/models/shapestyle.model';
import { SiterecordIdChange } from 'app/models/siterecord-id-change.model';
import {
  AuthorizationService,
  MapService,
  SiterecordCommentService,
  SiterecordService,
  StorageService,
  VildVersionService
} from 'app/services';
import { CbmInputStyle } from 'app/shared/input/cbm-input-style';

declare const L: any;

export abstract class DetailsBase implements OnInit {
  public errorMessages: CBMError[];

  protected map: L.Map;
  protected control: any;
  protected layerControl: any;
  protected legend: L.Control;
  protected contract: Contract;
  protected featureGroup: L.FeatureGroup;
  protected line: any;
  protected arrowHead: any;
  protected polyline: L.Polyline;

  public currentVildVersion: VildVersion;
  public futureVildVersion: VildVersion;
  public location: Siterecord;
  public locationList: any[];
  public originalLocation: any;
  public openlr: OpenLRExtendedPoint;
  public readonly: boolean;
  public isEditPage: boolean;
  public changelogRequested = false;
  public isSaving = false;
  public isCanceling = false;
  public isLoading = false;
  public isCurrentAlertcAdded: boolean;
  public isFutureAlertcAdded: boolean;

  protected greenMarker: any;
  protected blueMarker: any;
  protected redMarker: any;

  protected readonly ROUTING_POINTS_STYLE = new ShapeStyle('blue', 'rgb(0,0,255)', 8, 'routingPointsPane');
  protected readonly PREVIOUS_ROUTING_POINTS_STYLE = new ShapeStyle(
    'cyan',
    'rgb(0,255,255)',
    8,
    'previousRoutingPointsPane'
  );
  protected readonly ADMIN_ROUTING_POINTS_STYLE = new ShapeStyle(
    'green-dark',
    'rgb(0,128,0)',
    8,
    'adminRoutingPointsPane'
  );
  protected readonly FCD_SHAPE_STYLE = new ShapeStyle('blue', 'rgb(0,0,255)', 19, 'fcdRoutePane');
  protected readonly ORIGINAL_SHAPE_STYLE = new ShapeStyle('green-light', 'rgb(0,255,0)', 17, 'originalShapePane');
  protected readonly PREVIOUS_SHAPE_STYLE = new ShapeStyle('yellow', 'rgb(255,220,00)', 13, 'previousShapePane');
  protected readonly SHAPE_STYLE = new ShapeStyle('pink', 'rgb(255,0,255)', 6, 'shapePane');
  protected readonly NEW_SHAPE_STYLE = new ShapeStyle('red', 'rgb(255, 0, 0)', 3, 'newRoutePane');

  protected shapeLayer: L.Layer;
  protected previousShapeLayer: L.Layer;
  protected hectoPuntenLayer: L.Layer;

  protected newShapeTranslation: string;
  protected shapeTranslation: string;
  protected previousShapeTranslation: string;
  protected hectopuntenLayerTranslation: string;

  public previousWaypoints: any[];
  public noRouteFoundTranslation: string;
  public failedSaveTranslation: string;

  public singleColumnStyle: CbmInputStyle = { label: 'col-xs-2', control: 'col-xs-10' };
  public twoColumnStyle: CbmInputStyle = { label: 'col-xs-4', control: 'col-xs-8' };

  constructor(
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly authorizationService: AuthorizationService,
    protected readonly siterecordService: SiterecordService,
    protected readonly siterecordCommentService: SiterecordCommentService,
    protected readonly storageService: StorageService,
    protected readonly translateService: TranslateService,
    protected readonly mapService: MapService,
    protected readonly vildVersionService: VildVersionService
  ) {}

  protected abstract initializeTranslations(): void;

  protected abstract initializeControl(draggable: boolean): void;

  protected abstract initializeLegend(): void;

  protected abstract getLocationById(locationId: number): void;

  protected abstract onEdit(): void;

  protected abstract removeLayers(): void;

  ngOnInit(): void {
    this.initializeMap();
    this.initializeMarkers();
    this.initializeLayerNames();
    this.initializeTranslations();
    this.initializeLayerControl();
    this.initializeLegend();
    this.initializeVildVersions();

    this.route.params.subscribe(params => {
      this.resetMap();
      const locationType = params['locationType'];
      this.isEditPage = params['action'] === 'edit';
      const locationId = params['locationId'];
      this.locationList = this.storageService.readLocationListFromStorage();
      this.getLocationById(locationId);
    });

    this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      this.initializeLayerNames();
      this.initializeTranslations();
      this.initializeLayerControl();
      this.initializeLegend();
    });
  }

  private initializeMap(): void {
    this.map = L.map('map').setView([52.1, 5], 8);

    L.tileLayer(this.mapService.osmTilesUrl, { maxZoom: 20, maxNativeZoom: 18 }).addTo(this.map);
    this.hectoPuntenLayer = L.tileLayer(this.mapService.hectometreTilesUrl, { maxZoom: 19 });
    this.hectoPuntenLayer.addTo(this.map);

    this.featureGroup = new L.featureGroup();
    this.initializeControl(false);
  }

  private initializeLayerNames(): void {
    this.translate('SITERECORD.HECTOPUNTENLAYER').subscribe(
      translation => (this.hectopuntenLayerTranslation = translation)
    );
  }

  protected translate(valueToTranslate: string): Observable<string> {
    return this.translateService.get(valueToTranslate);
  }

  private initializeMarkers() {
    this.greenMarker = L.ExtraMarkers.icon({
      markerColor: 'green',
      shape: 'circle'
    });

    this.blueMarker = L.ExtraMarkers.icon({
      markerColor: 'blue',
      shape: 'circle'
    });

    this.redMarker = L.ExtraMarkers.icon({
      markerColor: 'red',
      shape: 'circle'
    });
  }

  protected initializeLayerControl(): void {
    if (!this.layerControl) {
      this.layerControl = L.control.layers({}, {}, { position: 'topright' }).addTo(this.map);
    } else {
      this.layerControl.removeLayer(this.hectoPuntenLayer);
    }
    this.layerControl.addOverlay(this.hectoPuntenLayer, this.hectopuntenLayerTranslation);
  }

  protected createLegend(labels: string[], colors: string[]): void {
    if (!this.legend) {
      this.legend = L.control({ position: 'bottomright' });
    } else {
      this.map.removeControl(this.legend);
    }

    this.legend.onAdd = e => {
      const div = L.DomUtil.create('div', 'info legend');

      // loop through our density intervals and generate a label with a colored square for each interval
      for (let i = 0; i < labels.length; i++) {
        div.innerHTML += `<i style="background:${colors[i]}"></i> ${labels[i]}<br />`;
      }
      return div;
    };
    this.legend.addTo(this.map);
  }

  protected resetMap(): void {
    if (this.featureGroup) {
      this.map.removeLayer(this.featureGroup);

      this.removeLayers();

      this.featureGroup = new L.featureGroup();
    }
  }

  protected redrawMap(): void {
    // Remove all layers and controls from map
    this.map.removeLayer(this.featureGroup);

    this.featureGroup.eachLayer((layer: L.Layer) => this.featureGroup.removeLayer(layer));

    this.removeLayers();

    // Redraw all layers and controls
    this.initializeControl(!this.readonly);
    this.getLocationById(this.location.id);
  }

  protected initializeReadonly(): void {
    if ((!this.isEditPage && this.isContractDataproviderTypeMst()) || this.contractIsRwsShapesContract()) {
      this.readonly = true;
    } else {
      this.readonly = !this.isEditPage;
    }
    if (!this.readonly) {
      this.onEdit();
    }
  }

  public initializeVildVersions() {
    this.vildVersionService.getActualVildVersions().subscribe(
      vildVersions => {
        this.currentVildVersion = vildVersions[0];
        this.futureVildVersion = vildVersions[1];
      },
      errors => console.log(errors)
    );
  }

  public errorFindingRoute(): void {
    if (this.previousWaypoints && this.previousWaypoints.length > 0) {
      this.control.setWaypoints(this.previousWaypoints);
    }
    alert(this.noRouteFoundTranslation);
  }

  public isContractTypeDataProviderOrFcdAndLocationAdmin() {
    return this.isContractTypeDataProvider() || (this.isContractTypeFCD() && this.isLocationAdmin());
  }

  public isLocationAdmin(): boolean {
    return this.authorizationService.isLocationAdmin();
  }

  public isMapAdmin(): boolean {
    return this.authorizationService.isMapAdmin();
  }

  public isContractTypeFCD(): boolean {
    if (this.location && this.location.contract) {
      return this.location.contract.contractType === ContractType.FCD;
    }
    return false;
  }

  public isContractTypeDataProvider(): boolean {
    if (this.location && this.location.contract) {
      return this.location.contract.contractType === ContractType.DataProvider;
    }
    return false;
  }

  public isContractDataproviderTypeIntekenen(): boolean {
    if (this.location && this.location.contract) {
      return this.location.contract.dataproviderType === DataProviderType.Intekenen;
    }
    return false;
  }

  public isContractDataproviderTypeMst(): boolean {
    if (this.location && this.location.contract) {
      return this.location.contract.dataproviderType === DataProviderType.MST;
    }
    return false;
  }

  public isFcdAndLinearLocationType(): boolean {
    return this.isContractTypeFCD() && this.location.locationType === LocationType.linear;
  }

  public contractIsRwsShapesContract(): boolean {
    if (this.location && this.location.contract) {
      return this.location.contract.prefix === 'RWS01';
    }
    return false;
  }

  public showBackButton(): boolean {
    return this.readonly || (this.isContractDataproviderTypeMst() && this.isLocationAdmin());
  }

  public showSaveAndCancelButtons(): boolean {
    return !this.readonly && this.isAllowedToEdit();
  }

  public isAllowedToEdit(): boolean {
    return this.isLocationAdmin() || this.isContractDataproviderTypeIntekenen() || this.isFcdAndLinearLocationType();
  }

  public isDeleted(): boolean {
    return this.location.deleted;
  }

  public isAllowedToEditRoute(): boolean {
    if (this.contractIsRwsShapesContract()) {
      return false;
    }
    return (
      this.isLocationAdmin() ||
      ((this.isContractDataproviderTypeIntekenen() || this.isContractTypeFCD()) &&
        (this.location.status === 'created' ||
          this.location.status === 'auto_validated_correct' ||
          this.location.status === 'auto_validated_error'))
    );
  }

  public isGeneralReadonly(): boolean {
    return this.readonly || this.isContractDataproviderTypeMst();
  }

  public isInShapeFileReadonly(): boolean {
    if (!this.isLocationAdmin()) {
      return true;
    }
    return this.readonly;
  }

  public isVildReadonly(): boolean {
    return this.readonly || !this.isLocationAdmin() || (this.isLocationAdmin() && this.isContractDataproviderTypeMst());
  }

  public haveCurrentLinearVild(): boolean {
    return this.location.currentAlertcLinears.length > 0;
  }

  public haveCurrentPointVild(): boolean {
    return this.location.currentAlertcPoints.length > 0;
  }

  public haveFutureLinearVild(): boolean {
    return this.location.futureAlertcLinears.length > 0;
  }

  public haveFuturePointVild(): boolean {
    return this.location.futureAlertcPoints.length > 0;
  }

  public errorCurrentVildVersion(): boolean {
    if (this.haveCurrentLinearVild()) {
      return this.location.currentAlertcLinears[0].vildEncodingStatus === VildEncodingStatusType.ERROR;
    } else if (this.haveCurrentPointVild()) {
      return this.location.currentAlertcPoints[0].vildEncodingStatus === VildEncodingStatusType.ERROR;
    }
    return true;
  }

  public errorFutureVildVersion(): boolean {
    if (this.haveFutureLinearVild()) {
      return this.location.futureAlertcLinears[0].vildEncodingStatus === VildEncodingStatusType.ERROR;
    } else if (this.haveFuturePointVild()) {
      return this.location.futureAlertcPoints[0].vildEncodingStatus === VildEncodingStatusType.ERROR;
    }
    return true;
  }

  public areaCurrentVildVersion(): boolean {
    if (this.haveCurrentLinearVild()) {
      return this.location.currentAlertcLinears[0].vildEncodingStatus === VildEncodingStatusType.AREA;
    } else if (this.haveCurrentPointVild()) {
      return this.location.currentAlertcPoints[0].vildEncodingStatus === VildEncodingStatusType.AREA;
    }
    return false;
  }

  public areaFutureVildVersion(): boolean {
    if (this.haveFutureLinearVild()) {
      return this.location.futureAlertcLinears[0].vildEncodingStatus === VildEncodingStatusType.AREA;
    } else if (this.haveFuturePointVild()) {
      return this.location.futureAlertcPoints[0].vildEncodingStatus === VildEncodingStatusType.AREA;
    }
    return false;
  }

  public manualCurrentVildVersion(): boolean {
    if (this.haveCurrentLinearVild()) {
      return (
        this.location.currentAlertcLinears.filter(ac => ac.vildEncodingStatus === VildEncodingStatusType.MANUAL)
          .length > 0
      );
    } else if (this.haveCurrentPointVild()) {
      return (
        this.location.currentAlertcPoints.filter(ac => ac.vildEncodingStatus === VildEncodingStatusType.MANUAL).length >
        0
      );
    }
    return false;
  }

  public manualFutureVildVersion(): boolean {
    if (this.haveFutureLinearVild()) {
      return (
        this.location.futureAlertcLinears.filter(ac => ac.vildEncodingStatus === VildEncodingStatusType.MANUAL).length >
        0
      );
    } else if (this.haveFuturePointVild()) {
      return (
        this.location.futureAlertcPoints.filter(ac => ac.vildEncodingStatus === VildEncodingStatusType.MANUAL).length >
        0
      );
    }
    return false;
  }

  public errorFcd(): boolean {
    return this.location.fcdEncodingStatus === FcdEncodingStatusType.ERROR;
  }

  public manualFcd(): boolean {
    return this.location.fcdEncodingStatus === FcdEncodingStatusType.MANUAL;
  }

  protected requestChangelog(): void {
    if (this.isContractTypeDataProviderOrFcdAndLocationAdmin() && !this.changelogRequested) {
      this.changelogRequested = true;
    }
  }

  public changeReadonly(): void {
    this.readonly = !this.readonly;
    this.onEdit();
  }

  public goBackToOverview(): void {
    if (this.storageService.getLocationOverviewOrigin() === LocationOverviewType.LOCATION_OVERVIEW_ADMIN) {
      this.router.navigate([`/admin/locations`]);
    } else {
      this.router.navigate([`/locations/${this.location.contractId}`]);
    }
  }

  public hasPreviousLocation(): boolean {
    const li: number = this.locationList.findIndex(n => n.id === this.location.id);
    return li > 0;
  }

  public hasNextLocation(): boolean {
    const li: number = this.locationList.findIndex(n => n.id === this.location.id);
    return li !== -1 && li < this.locationList.length - 1;
  }

  public previousLocation(): void {
    const li: number = this.locationList.findIndex(n => n.id === this.location.id);
    if (li > 0) {
      const previousLocation = this.locationList[li - 1];
      this.storageService.setLocationListInStorage(this.locationList);
      this.router.navigate([`/location/${previousLocation.locationType}/detail/${previousLocation.id}`]);
    }
  }

  public nextLocation(): void {
    const li: number = this.locationList.findIndex(n => n.id === this.location.id);
    if (li !== -1 && li < this.locationList.length - 1) {
      const nextLocation = this.locationList[li + 1];
      this.storageService.setLocationListInStorage(this.locationList);
      this.router.navigate([`/location/${nextLocation.locationType}/detail/${nextLocation.id}`]);
    }
  }

  public onStatusChange(status: ConfigurationStatusType) {
    this.siterecordService.updateStatusSiterecord(this.location.id, status).subscribe(
      response => this.handlePage(),
      (errors: CBMError[]) => {
        this.errorMessages = errors;
      }
    );
  }

  public updateSiterecordAndStatus(copyOfLocation: any, status: ConfigurationStatusType) {
    this.siterecordService
      .updateSiterecord(copyOfLocation)
      .mergeMap(
        response => this.siterecordService.updateStatusSiterecord(this.location.id, status),
        errors => {
          this.handleError(errors);
          this.isSaving = false;
        }
      )
      .subscribe(
        response => {
          this.isSaving = false;
          this.handlePage();
        },
        (errors: CBMError[]) => {
          this.errorMessages = errors;
        }
      );
  }

  public handlePage(): void {
    if (!this.isEditPage) {
      this.readonly = true;
      this.redrawMap();
    } else {
      this.goBackToOverview();
    }
  }

  public handleError(errors: any) {
    console.log(errors);
    if (errors.length === 1 && errors[0].code === 500) {
      alert(this.failedSaveTranslation);
    } else {
      this.errorMessages = errors;
    }
  }

  public updateSiterecord(copyOfLocation: any): void {
    this.siterecordService.updateSiterecord(copyOfLocation).subscribe(
      response => {
        if (response.ok) {
          this.handlePage();
          this.isSaving = false;
        }
      },
      (errors: CBMError[]) => {
        this.handleError(errors);
        this.isSaving = false;
      }
    );
  }

  public updateSiterecordWithComment(copyOfLocation: Siterecord, comment: string, isInternal: boolean): void {
    this.siterecordService.updateSiterecord(copyOfLocation).subscribe(
      response => {
        if (response.ok) {
          const siterecordComment: SiterecordComment = {
            id: null,
            comment,
            internal: isInternal,
            creationDateTime: new Date(),
            creationUser: null
          };

          this.postComment(siterecordComment);
          this.handlePage();
          this.isSaving = false;
        }
      },
      (errors: CBMError[]) => {
        this.handleError(errors);
        this.isSaving = false;
      }
    );
  }

  public getComments(): void {
    this.siterecordCommentService.getComments(this.location.id).subscribe(
      (comments: SiterecordComment[]) => (this.location.siterecordComments = comments),
      errors => console.log(errors)
    );
  }

  public postComment(comment: SiterecordComment): void {
    this.siterecordCommentService.postComment(comment, this.location.id).subscribe(
      res => this.getComments(),
      errors => console.log(errors)
    );
  }

  public updateRecordId($event: SiterecordIdChange) {
    const copyOfLocation = JSON.parse(this.originalLocation);
    const { id: recordId, comment, isInternal } = $event;

    if (recordId && comment) {
      copyOfLocation.recordId = recordId;
      this.updateSiterecordWithComment(copyOfLocation, comment, isInternal);
    }
  }

  public rollbackDeletedStatus(id: number) {
    this.siterecordService.rollbackDeleteSiterecord(id).subscribe(
      res => this.handlePage(),
      errors => console.log(errors)
    );
  }

  public isRecordIdEditable(): boolean {
    const contract = this.location.contract;
    const isMapAdmin = this.authorizationService.isMapAdmin();
    const isContractTypeFcd = contract.contractType === ContractType.FCD;
    const isContractTypeDataprovider =
      contract.contractType === ContractType.DataProvider && contract.dataproviderType === DataProviderType.Intekenen;
    const isEditableRecordId = !this.location.ncisExported && (isContractTypeFcd || isContractTypeDataprovider);

    return isMapAdmin || isEditableRecordId;
  }

  public isCoordinatesEditable(): boolean {
    const contract = this.location.contract;
    const isMapAdmin = this.authorizationService.isMapAdmin();
    const isContractTypeDataprovider =
      contract.contractType === ContractType.DataProvider && contract.dataproviderType === DataProviderType.Intekenen;

    return isMapAdmin || isContractTypeDataprovider;
  }

  abstract onSaveAndApprove(status: ConfigurationStatusType): void;

  abstract onCancel(): void;

  abstract onSave(): void;

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent($event: KeyboardEvent) {
    if ($event.ctrlKey) {
      this.readonly ? this.handleKeyboardEventWhileViewing($event) : this.handleKeyboardEventWhileEditing($event);
    }
  }

  private handleKeyboardEventWhileViewing = ($event: KeyboardEvent): void => {
    switch ($event.key) {
      case 'ArrowRight': {
        if (this.readonly && this.hasNextLocation()) {
          this.preventDefaultForCbmShortcut($event);
          this.nextLocation();
        }
        return;
      }
      case 'ArrowLeft': {
        if (this.readonly && this.hasPreviousLocation()) {
          this.preventDefaultForCbmShortcut($event);
          this.previousLocation();
        }
        return;
      }
      case 'ArrowUp': {
        if (this.showBackButton()) {
          this.preventDefaultForCbmShortcut($event);
          this.goBackToOverview();
        }
        return;
      }
      case 'b': {
        if (this.isAllowedToEdit() && this.readonly && !this.isDeleted()) {
          this.preventDefaultForCbmShortcut($event);
          this.changeReadonly();
        }
        return;
      }
    }
  }

  private handleKeyboardEventWhileEditing = ($event: KeyboardEvent): void => {
    switch ($event.key) {
      case 's': {
        if (!this.readonly && this.showSaveAndCancelButtons()) {
          this.preventDefaultForCbmShortcut($event);
          this.onSave();
        }
        return;
      }
      case 'z': {
        if (!this.readonly && this.showSaveAndCancelButtons()) {
          this.preventDefaultForCbmShortcut($event);
          this.onCancel();
        }
        return;
      }
      case 'g': {
        if (!this.readonly && this.isLocationAdmin()) {
          this.preventDefaultForCbmShortcut($event);
          this.onStatusChange(ConfigurationStatusType.CORRECT);
        }
        return;
      }
      case 'o': {
        if (!this.readonly && this.isLocationAdmin()) {
          this.preventDefaultForCbmShortcut($event);
          this.onSaveAndApprove(ConfigurationStatusType.CORRECT);
        }
        return;
      }
      case 'd': {
        if (!this.readonly && this.isLocationAdmin()) {
          this.preventDefaultForCbmShortcut($event);
          this.onStatusChange(ConfigurationStatusType.ERROR);
        }
        return;
      }
    }
  }

  private preventDefaultForCbmShortcut($event: KeyboardEvent): void {
    $event.preventDefault();
  }

  public calculateDirectionCoordinatePair(lat: string, lon: string, inputBearing: number): string {
    const latNum = parseFloat(lat);
    const lonNum = parseFloat(lon);

    const inputPoint = [lonNum, latNum];
    const distanceInKilometers = 0.01;

    const destinationPoint = destination(inputPoint, distanceInKilometers, inputBearing);
    return `${lon}\\,${lat}|${destinationPoint.geometry.coordinates[0]}\\,${destinationPoint.geometry.coordinates[1]}`;
  }

  public calculateBearing(startLat: number, startLon: number, dirLat: number, dirLon) {
    const startPoint = point([startLon, startLat]);
    const directionPoint = point([dirLon, dirLat]);

    const b = bearing(startPoint, directionPoint);
    const maxBearing = 360;

    if (b < 0) {
      return Math.round(maxBearing + b);
    } else {
      return Math.round(b);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/locations/details/detailsbase.ts